import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  MenuItem, 
  Select, 
  FormControl, 
  Box, 
  Paper, 
  Typography, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  InputLabel,
  Tooltip,
  Grid,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  IconButton,
  CircularProgress
} from '@mui/material';
import { fetchAdminUsers, updateUserType, updateUserData, selectAllAdminUsers } from 'redux/admin/adminUserSlice';
import { enqueueSnackbar } from 'notistack';
import EditIcon from '@mui/icons-material/Edit';

/**
 * UsersPage Component
 * Admin page for viewing and managing users
 */
export default function UsersPage() {
  const dispatch = useDispatch();
  const users = useSelector(selectAllAdminUsers);
  const loading = useSelector(state => state.adminUser.loading);
  const currentUser = useSelector(state => state.userProfile.data);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userData, setUserData] = useState({
    user_type: '',
    kyc_status: '-',
    marketing: false,
    stripe_customer_id: ''
  });
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [localFilter, setLocalFilter] = useState({
    userType: '',
    username: '',
    email: ''
  });

  // Fetch users on mount
  useEffect(() => {
    dispatch(fetchAdminUsers());
  }, [dispatch]);

  // Initialize filtered users when users load
  useEffect(() => {
    if (users && users.length > 0) {
      setFilteredUsers([...users]);
    }
  }, [users]);

  // Apply filters when localFilter changes
  useEffect(() => {
    if (users && users.length > 0) {
      applyFilters();
    }
  }, [localFilter]);

  // Handle filter input changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setLocalFilter(prev => ({ ...prev, [name]: value }));
  };

  // Apply filters
  const applyFilters = () => {
    let filtered = [...users];
    
    if (localFilter.userType) {
      filtered = filtered.filter(user => {
        const userType = user.user_type || '';
        return userType === localFilter.userType;
      });
    }
    
    if (localFilter.username) {
      const usernameSearch = localFilter.username.toLowerCase();
      filtered = filtered.filter(user => {
        const username = user.username || '';
        return username.toLowerCase().includes(usernameSearch);
      });
    }
    
    if (localFilter.email) {
      const emailSearch = localFilter.email.toLowerCase();
      filtered = filtered.filter(user => {
        const email = user.email || '';
        return email.toLowerCase().includes(emailSearch);
      });
    }
    
    setFilteredUsers(filtered);
  };

  // Reset filters
  const handleResetFilters = () => {
    setLocalFilter({
      userType: '',
      username: '',
      email: ''
    });
    // Reset to show all users
    setFilteredUsers([...users]);
  };

  // Open the edit modal
  const handleOpenModal = (user) => {
    setSelectedUser(user);
    setUserData({
      user_type: user.user_type || '',
      kyc_status: user.kyc_status || '-',
      marketing: user.marketing || false,
      stripe_customer_id: user.stripe_customer_id || ''
    });
    setModalOpen(true);
  };

  // Close the edit modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedUser(null);
  };

  // Handle user data change
  const handleUserDataChange = (field, value) => {
    setUserData(prev => ({ ...prev, [field]: value }));
  };

  // Handle user update
  const handleUpdateUser = () => {
    if (!selectedUser) return;
    
    const isSelf = isCurrentAdmin(selectedUser.id);
    const updatedData = { ...userData };
    
    // If self-editing, don't allow changing user_type
    if (isSelf) {
      updatedData.user_type = selectedUser.user_type;
    }
    
    dispatch(updateUserData({ 
      userId: selectedUser.id, 
      userData: updatedData
    }))
      .unwrap()
      .then(() => {
        enqueueSnackbar('User updated successfully', { variant: 'success' });
        handleCloseModal();
      })
      .catch((error) => {
        enqueueSnackbar(`Failed to update user: ${error.message}`, { variant: 'error' });
      });
  };

  // Check if user is the current admin
  const isCurrentAdmin = (userId) => {
    return currentUser && currentUser.id === userId;
  };

  // Column headers for the custom table
  const columnHeaders = [
    { id: 'id', label: 'ID', width: '70px' },
    { id: 'username', label: 'Username', width: '200px' },
    { id: 'email', label: 'Email', width: '250px' },
    { id: 'first_name', label: 'First Name', width: '120px' },
    { id: 'last_name', label: 'Last Name', width: '120px' },
    { id: 'mobile_phone', label: 'Phone', width: '120px' },
    { id: 'kyc_status', label: 'KYC Status', width: '120px' },
    { id: 'user_type', label: 'User Type', width: '100px' },
    { id: 'actions', label: 'Actions', width: 'auto' },
  ];

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: 'auto'
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>User Management</Typography>
      
      {/* Filter Controls */}
      <Paper 
        elevation={1} 
        sx={{ 
          p: 2, 
          mb: 3,
          width: '100%'
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Filter Users</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Username"
              name="username"
              value={localFilter.username}
              onChange={handleFilterChange}
              placeholder="Search by username"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              value={localFilter.email}
              onChange={handleFilterChange}
              placeholder="Search by email"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>User Type</InputLabel>
              <Select
                name="userType"
                value={localFilter.userType}
                onChange={handleFilterChange}
                label="User Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="INVESTOR">Investor</MenuItem>
                <MenuItem value="TRADER">Trader</MenuItem>
                <MenuItem value="ADMIN">Admin</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                variant="outlined" 
                onClick={handleResetFilters}
                size="small"
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Users Table */}
      <Box sx={{ 
        width: '100%', 
        overflow: 'auto',
        border: '1px solid rgba(224, 224, 224, 1)',
        borderRadius: 1,
      }}>
        <Box sx={{ 
          minWidth: 1000, 
          width: '100%',
        }}>
          {/* Table Header */}
          <Table sx={{ 
            width: '100%',
            tableLayout: 'fixed',
          }} size="small">
            <TableHead sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>
              <TableRow sx={{ width: '100%' }}>
                {columnHeaders.map((column) => (
                  <TableCell 
                    key={column.id} 
                    sx={{ 
                      width: column.width,
                      ...(column.id === 'actions' && { flex: 1 }),
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontWeight: 'bold',
                      backgroundColor: (theme) => theme.palette.grey[100],
                      py: 0.5,
                      ...(column.id === 'actions' && { textAlign: 'right' })
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          </Table>
          
          {/* Loading indicator */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            /* User rows */
            <Box sx={{ width: '100%' }}>
              {filteredUsers.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1">No users found. Try adjusting your filters.</Typography>
                </Box>
              ) : (
                filteredUsers.map(user => (
                  <Box sx={{ width: '100%' }} key={user.id}>
                    <Table sx={{ tableLayout: 'fixed', width: '100%' }} size="small">
                      <TableBody>
                        <TableRow 
                          sx={{ 
                            display: 'flex', 
                            width: '100%',
                            alignItems: 'center',
                            borderBottom: '1px solid rgba(224, 224, 224, 1)',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          <TableCell sx={{ width: '70px', borderBottom: 'none', flexShrink: 0 }}>
                            {user.id}
                          </TableCell>
                          <TableCell 
                            sx={{ 
                              width: '200px', 
                              borderBottom: 'none', 
                              flexShrink: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <Tooltip title={user.username || 'N/A'}>
                              <span>{user.username || 'N/A'}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell 
                            sx={{ 
                              width: '250px', 
                              borderBottom: 'none', 
                              flexShrink: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <Tooltip title={user.email || 'N/A'}>
                              <span>{user.email || 'N/A'}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell 
                            sx={{ 
                              width: '120px', 
                              borderBottom: 'none', 
                              flexShrink: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <Tooltip title={user.first_name || 'N/A'}>
                              <span>{user.first_name || 'N/A'}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell 
                            sx={{ 
                              width: '120px', 
                              borderBottom: 'none', 
                              flexShrink: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <Tooltip title={user.last_name || 'N/A'}>
                              <span>{user.last_name || 'N/A'}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell 
                            sx={{ 
                              width: '120px', 
                              borderBottom: 'none', 
                              flexShrink: 0
                            }}
                          >
                            {user.mobile_phone || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ width: '120px', borderBottom: 'none', flexShrink: 0 }}>
                            {(() => {
                              const status = user.kyc_status || '-';
                              let color = 'default';
                              if (status === 'verified') color = 'success';
                              if (status === 'pending') color = 'warning';
                              
                              return (
                                <Chip 
                                  label={status.toUpperCase()} 
                                  color={color} 
                                  size="small" 
                                />
                              );
                            })()}
                          </TableCell>
                          <TableCell sx={{ width: '100px', borderBottom: 'none', flexShrink: 0 }}>
                            {user.user_type || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ borderBottom: 'none', flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                            <Tooltip title={isCurrentAdmin(user.id) ? "You can edit your own record except user type" : "Edit user"}>
                              <span>
                                <Button 
                                  startIcon={<EditIcon />} 
                                  size="small"
                                  onClick={() => handleOpenModal(user)}
                                  variant="outlined"
                                  color="primary"
                                >
                                  Edit
                                </Button>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                ))
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Edit User Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Username:</strong> {selectedUser.username}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Email:</strong> {selectedUser.email}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="user-type-label">User Type</InputLabel>
                    <Select
                      labelId="user-type-label"
                      value={userData.user_type}
                      label="User Type"
                      onChange={(e) => handleUserDataChange('user_type', e.target.value)}
                      disabled={isCurrentAdmin(selectedUser.id)}
                    >
                      <MenuItem value="INVESTOR">Investor</MenuItem>
                      <MenuItem value="TRADER">Trader</MenuItem>
                      <MenuItem value="ADMIN">Admin</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="kyc-status-label">KYC Status</InputLabel>
                    <Select
                      labelId="kyc-status-label"
                      value={userData.kyc_status}
                      label="KYC Status"
                      onChange={(e) => handleUserDataChange('kyc_status', e.target.value)}
                    >
                      <MenuItem value="-">Not Started</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="verified">Verified</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="marketing-label">Marketing Opt-in</InputLabel>
                    <Select
                      labelId="marketing-label"
                      value={userData.marketing}
                      label="Marketing Opt-in"
                      onChange={(e) => handleUserDataChange('marketing', e.target.value)}
                    >
                      <MenuItem value={true}>Yes</MenuItem>
                      <MenuItem value={false}>No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Stripe Customer ID"
                    value={userData.stripe_customer_id || ''}
                    onChange={(e) => handleUserDataChange('stripe_customer_id', e.target.value)}
                    placeholder="Stripe Customer ID"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button onClick={handleUpdateUser} variant="contained" color="secondary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
} 